'use client';

/**
 * ImportWizard Component
 *
 * A multi-step import wizard for CSV and JSON file imports.
 * Provides file upload, field mapping, validation, and progress tracking.
 *
 * Note: Excel support requires external library - pass custom acceptedFileTypes
 * and handle parsing in the onImport callback.
 *
 * Steps:
 * 1. File Upload - Drag-drop file selection with preview
 * 2. Field Mapping - Map source columns to target fields with auto-suggestions
 * 3. Validation - Review validation errors and warnings
 * 4. Import Progress - Real-time import progress with status updates
 * 5. Import Summary - Final results with success/error counts
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import type {
  FieldDefinition,
  FieldMapping,
  ImportConfig,
  ImportResult,
  ImportValidationResult,
} from '../../types/entityFramework';
import { StepIndicator, type StepIndicatorStep } from '../FormWizard';
import { GlassButton } from '../GlassButton';
import { GlassCard } from '../GlassCard';
import { FieldMappingTable } from './FieldMappingTable';
import { FileDropzone } from './FileDropzone';
import { ImportProgress } from './ImportProgress';
import { ValidationResults } from './ValidationResults';

// ============================================================================
// Types
// ============================================================================

export interface ImportWizardProps {
  /**
   * Entity field definitions to map to
   */
  entityFields: FieldDefinition[];

  /**
   * Callback to perform the import
   */
  onImport: (config: ImportConfig, file: File) => Promise<ImportResult>;

  /**
   * Optional callback to validate before import
   */
  onValidate?: (config: ImportConfig, file: File) => Promise<ImportValidationResult>;

  /**
   * Callback when wizard is cancelled
   */
  onCancel?: () => void;

  /**
   * Accepted file types
   * @default [".csv", ".json"]
   * Note: Excel (.xlsx, .xls) support requires external library - use onImport callback for custom parsing
   */
  acceptedFileTypes?: string[];

  /**
   * Maximum file size in bytes
   * @default 10MB
   */
  maxFileSize?: number;

  /**
   * Neon accent color
   * @default "cyan"
   */
  neonAccent?: NeonAccent;

  /**
   * Additional class name
   */
  className?: string;
}

type WizardStep = 'upload' | 'mapping' | 'validation' | 'importing' | 'complete';

interface ParsedFileData {
  columns: string[];
  rows: Record<string, unknown>[];
  preview: Record<string, unknown>[];
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  width: '100%',
});

const StyledStepContent = styled(motion.div)({
  width: '100%',
});

const StyledActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(2),
}));

const StyledLeftActions = styled(Box)({
  display: 'flex',
  gap: 12,
});

const StyledRightActions = styled(Box)({
  display: 'flex',
  gap: 12,
});

const StyledPreviewTable = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    width: '100%',
    overflowX: 'auto',
    borderRadius: 12,
    border: `1px solid ${glass.border}`,
    backgroundColor: glass.background,
    marginTop: 16,

    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.8125rem',
    },

    '& th, & td': {
      padding: '10px 16px',
      textAlign: 'left',
      borderBottom: `1px solid ${$isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
      whiteSpace: 'nowrap',
      maxWidth: 200,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    '& th': {
      fontWeight: 600,
      backgroundColor: $isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    },

    '& tr:last-child td': {
      borderBottom: 'none',
    },
  };
});

// ============================================================================
// Step Configuration
// ============================================================================

const WIZARD_STEPS: StepIndicatorStep[] = [
  { id: 'upload', title: 'Upload File', description: 'Select your data file' },
  {
    id: 'mapping',
    title: 'Map Fields',
    description: 'Match columns to fields',
  },
  { id: 'validation', title: 'Validate', description: 'Review data quality' },
  { id: 'importing', title: 'Import', description: 'Processing records' },
  { id: 'complete', title: 'Complete', description: 'Import summary' },
];

const STEP_ORDER: WizardStep[] = ['upload', 'mapping', 'validation', 'importing', 'complete'];

// Animation variants
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -50 : 50,
    opacity: 0,
  }),
};

// ============================================================================
// File Parsing Helpers
// ============================================================================

/**
 * Parse a single CSV line handling RFC 4180 compliant quoted fields
 * Handles: commas in quoted fields, escaped quotes (""), proper field boundaries
 */
const parseCSVLine = (line: string): string[] => {
  const fields: string[] = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        // Check for escaped quote ("")
        if (i + 1 < line.length && line[i + 1] === '"') {
          currentField += '"';
          i += 2;
          continue;
        }
        // End of quoted field
        inQuotes = false;
        i++;
        continue;
      }
      currentField += char;
      i++;
    } else {
      if (char === '"') {
        // Start of quoted field
        inQuotes = true;
        i++;
        continue;
      }
      if (char === ',') {
        // End of field
        fields.push(currentField.trim());
        currentField = '';
        i++;
        continue;
      }
      currentField += char;
      i++;
    }
  }

  // Add the last field
  fields.push(currentField.trim());
  return fields;
};

/**
 * Parse CSV content (RFC 4180 compliant)
 * Handles quoted fields with commas and escaped quotes
 */
const parseCSV = (content: string): ParsedFileData => {
  // Normalize line endings
  const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedContent.trim().split('\n');

  if (lines.length === 0) {
    return { columns: [], rows: [], preview: [] };
  }

  // Parse header using RFC 4180 compliant parser
  const columns = parseCSVLine(lines[0]);

  // Parse rows
  const rows: Record<string, unknown>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue; // Skip empty lines

    const values = parseCSVLine(line);
    const row: Record<string, unknown> = {};
    columns.forEach((col, idx) => {
      row[col] = values[idx] ?? '';
    });
    rows.push(row);
  }

  return {
    columns,
    rows,
    preview: rows.slice(0, 5),
  };
};

/**
 * Parse JSON content
 */
const parseJSON = (content: string): ParsedFileData => {
  const data = JSON.parse(content);
  const rows = Array.isArray(data) ? data : [data];

  if (rows.length === 0) {
    return { columns: [], rows: [], preview: [] };
  }

  const columns = Object.keys(rows[0] || {});

  return {
    columns,
    rows,
    preview: rows.slice(0, 5),
  };
};

/**
 * Read and parse file
 */
const readFile = async (file: File): Promise<ParsedFileData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const ext = file.name.split('.').pop()?.toLowerCase();

        if (ext === 'csv') {
          resolve(parseCSV(content));
        } else if (ext === 'json') {
          resolve(parseJSON(content));
        } else if (ext === 'xlsx' || ext === 'xls') {
          // Excel files require external library like xlsx
          // Provide clear error message guiding users to use CSV/JSON
          reject(
            new Error('Excel files (.xlsx, .xls) require external parsing. Please export as CSV or use JSON format.'),
          );
        } else {
          reject(new Error('Unsupported file type'));
        }
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

// ============================================================================
// Component
// ============================================================================

export const ImportWizard = forwardRef<HTMLDivElement, ImportWizardProps>(
  (
    {
      entityFields,
      onImport,
      onValidate,
      onCancel,
      acceptedFileTypes = ['.csv', '.json'],
      maxFileSize = 10 * 1024 * 1024,
      neonAccent = 'cyan',
      className,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // State
    const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
    const [direction, setDirection] = useState(0);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [parsedData, setParsedData] = useState<ParsedFileData | null>(null);
    const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
    const [validationResult, setValidationResult] = useState<ImportValidationResult | null>(null);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [importProgress, setImportProgress] = useState(0);
    const [importStatus, setImportStatus] = useState('');
    const [processedRows, setProcessedRows] = useState(0);
    const [isValidating, setIsValidating] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Step index
    const currentStepIndex = STEP_ORDER.indexOf(currentStep);

    // Navigation helpers
    const canGoBack = currentStepIndex > 0 && !isImporting;
    const canGoNext = !isImporting && !isValidating && (currentStep === 'upload' ? !!selectedFile : true);

    // Handle file selection
    const handleFileSelect = useCallback(async (file: File) => {
      setError(null);
      setSelectedFile(file);

      try {
        const data = await readFile(file);
        setParsedData(data);
        setFieldMappings([]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse file');
        setParsedData(null);
      }
    }, []);

    // Handle step navigation
    const goToStep = useCallback(
      (step: WizardStep) => {
        const newIndex = STEP_ORDER.indexOf(step);
        setDirection(newIndex > currentStepIndex ? 1 : -1);
        setCurrentStep(step);
      },
      [currentStepIndex],
    );

    const handleNext = useCallback(async () => {
      if (!canGoNext) return;

      const nextIndex = currentStepIndex + 1;
      if (nextIndex >= STEP_ORDER.length) return;

      const nextStep = STEP_ORDER[nextIndex];

      // Handle validation step
      if (nextStep === 'validation' && onValidate && selectedFile && parsedData) {
        setIsValidating(true);
        setError(null);

        try {
          const config: ImportConfig = {
            entityId: 'temp',
            file: selectedFile,
            fieldMappings,
            options: {
              skipDuplicates: true,
              batchSize: 100,
              dryRun: true,
            },
          };

          const result = await onValidate(config, selectedFile);
          setValidationResult(result);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Validation failed');
        } finally {
          setIsValidating(false);
        }
      }

      // Handle import step
      if (nextStep === 'importing' && selectedFile) {
        setIsImporting(true);
        setImportProgress(0);
        setProcessedRows(0);
        setImportStatus('Preparing import...');
        setError(null);

        try {
          const config: ImportConfig = {
            entityId: 'temp',
            file: selectedFile,
            fieldMappings,
            options: {
              skipDuplicates: true,
              batchSize: 100,
            },
          };

          // Simulate progress updates
          const totalRows = parsedData?.rows.length || 0;
          const progressInterval = setInterval(() => {
            setImportProgress((prev) => {
              const next = Math.min(prev + Math.random() * 15, 95);
              setProcessedRows(Math.floor((next / 100) * totalRows));
              setImportStatus(`Importing records... (${Math.floor(next)}%)`);
              return next;
            });
          }, 200);

          const result = await onImport(config, selectedFile);

          clearInterval(progressInterval);
          setImportProgress(100);
          setProcessedRows(totalRows);
          setImportStatus('Import complete!');
          setImportResult(result);

          // Move to complete step after a brief delay
          setTimeout(() => {
            setDirection(1);
            setCurrentStep('complete');
            setIsImporting(false);
          }, 500);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Import failed');
          setIsImporting(false);
          setImportProgress(0);
        }

        return;
      }

      setDirection(1);
      setCurrentStep(nextStep);
    }, [canGoNext, currentStepIndex, onValidate, onImport, selectedFile, parsedData, fieldMappings]);

    const handleBack = useCallback(() => {
      if (!canGoBack) return;

      const prevIndex = currentStepIndex - 1;
      if (prevIndex < 0) return;

      setDirection(-1);
      setCurrentStep(STEP_ORDER[prevIndex]);
    }, [canGoBack, currentStepIndex]);

    // Check if required fields are mapped
    const hasRequiredFieldsMapped = useMemo(() => {
      const requiredFields = entityFields.filter((f) => f.required);
      const mappedTargets = new Set(fieldMappings.map((m) => m.targetField));
      return requiredFields.every((f) => mappedTargets.has(f.name));
    }, [entityFields, fieldMappings]);

    // Render step content
    const renderStepContent = () => {
      switch (currentStep) {
        case 'upload':
          return (
            <Box>
              <FileDropzone
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                acceptedFileTypes={acceptedFileTypes}
                maxFileSize={maxFileSize}
                error={error || undefined}
                neonAccent={neonAccent}
              />
              {/* File Preview */}
              {parsedData && parsedData.preview.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Preview (first 5 rows)
                  </Typography>

                  <StyledPreviewTable $isDark={isDark}>
                    <table>
                      <thead>
                        <tr>
                          {parsedData.columns.map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData.preview.map((row, idx) => (
                          <tr key={idx}>
                            {parsedData.columns.map((col) => (
                              <td key={col}>{String(row[col] ?? '')}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </StyledPreviewTable>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      mt: 1,
                      display: 'block',
                    }}
                  >
                    {parsedData.rows.length.toLocaleString()} total rows detected
                  </Typography>
                </Box>
              )}
            </Box>
          );

        case 'mapping':
          return parsedData ? (
            <FieldMappingTable
              sourceColumns={parsedData.columns}
              targetFields={entityFields}
              mappings={fieldMappings}
              onMappingsChange={setFieldMappings}
              previewData={parsedData.preview}
              neonAccent={neonAccent}
            />
          ) : (
            <Typography
              sx={{
                color: 'text.secondary',
              }}
            >
              No file data available
            </Typography>
          );

        case 'validation':
          return validationResult ? (
            <ValidationResults validationResult={validationResult} neonAccent={neonAccent} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
              }}
            >
              {isValidating ? (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Validating...
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Checking your data for errors and warnings
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Ready to Validate
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Click "Next" to validate your data
                  </Typography>
                </>
              )}
            </Box>
          );

        case 'importing':
        case 'complete':
          return (
            <ImportProgress
              progress={importProgress}
              status={importStatus}
              totalRows={parsedData?.rows.length || 0}
              processedRows={processedRows}
              isComplete={currentStep === 'complete'}
              result={importResult || undefined}
              neonAccent={neonAccent}
            />
          );

        default:
          return null;
      }
    };

    // Get button labels based on step
    const getNextLabel = () => {
      switch (currentStep) {
        case 'upload':
          return 'Continue to Mapping';
        case 'mapping':
          return onValidate ? 'Validate Data' : 'Start Import';
        case 'validation':
          return 'Start Import';
        case 'complete':
          return 'Done';
        default:
          return 'Next';
      }
    };

    // Check if next is disabled
    const isNextDisabled = (): boolean => {
      if (isValidating || isImporting) return true;
      if (currentStep === 'upload') return !selectedFile || !parsedData;
      if (currentStep === 'mapping') return fieldMappings.length === 0;
      if (currentStep === 'validation') return validationResult !== null && !validationResult.isValid;
      return false;
    };

    return (
      <StyledContainer ref={ref} className={className}>
        {/* Step Indicator */}
        <StepIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStepIndex}
          clickable={!isImporting && !isValidating}
          neonAccent={neonAccent}
          onStepClick={(idx) => {
            if (idx < currentStepIndex) {
              goToStep(STEP_ORDER[idx]);
            }
          }}
        />
        {/* Step Content */}
        <GlassCard neonAccent={neonAccent} glowOnHover={false}>
          {/* Step Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              {WIZARD_STEPS[currentStepIndex]?.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {WIZARD_STEPS[currentStepIndex]?.description}
            </Typography>
          </Box>

          {/* Animated Step Content */}
          <AnimatePresence mode="wait" custom={direction}>
            <StyledStepContent
              key={currentStep}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
            >
              {renderStepContent()}
            </StyledStepContent>
          </AnimatePresence>

          {/* Error Display */}
          {error && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                border: `1px solid ${theme.palette.error.main}`,
              }}
            >
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}

          {/* Warning for unmapped required fields */}
          {currentStep === 'mapping' && !hasRequiredFieldsMapped && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)',
                border: `1px solid ${theme.palette.warning.main}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'warning.main',
                }}
              >
                Some required fields are not mapped. Please ensure all required fields have source columns mapped.
              </Typography>
            </Box>
          )}

          {/* Actions */}
          {currentStep !== 'importing' && (
            <StyledActions>
              <StyledLeftActions>
                {onCancel && currentStep !== 'complete' && (
                  <GlassButton
                    variant="ghost"
                    color="secondary"
                    onClick={onCancel}
                    disabled={isImporting || isValidating}
                  >
                    Cancel
                  </GlassButton>
                )}
              </StyledLeftActions>

              <StyledRightActions>
                {canGoBack && currentStep !== 'complete' && (
                  <GlassButton variant="outlined" color="secondary" onClick={handleBack} disabled={isValidating}>
                    Back
                  </GlassButton>
                )}

                {currentStep === 'complete' ? (
                  onCancel && (
                    <GlassButton variant="gradient" onClick={onCancel}>
                      Done
                    </GlassButton>
                  )
                ) : (
                  <GlassButton
                    variant="gradient"
                    onClick={handleNext}
                    disabled={isNextDisabled()}
                    loading={isValidating}
                  >
                    {getNextLabel()}
                  </GlassButton>
                )}
              </StyledRightActions>
            </StyledActions>
          )}
        </GlassCard>
      </StyledContainer>
    );
  },
);

ImportWizard.displayName = 'ImportWizard';

export default ImportWizard;
